"use client"

import React, { useState, useEffect } from "react"
import { X, Phone, Mail, MapPin, ShoppingBag, Star, Clock, Edit2, Plus, Trash2, Home, Briefcase, Building, Heart, ChevronDown, ChevronUp } from "lucide-react"
import { useLanguage } from "../contexts/LanguageContext"
import { useToast } from "../components/ui/use-toast"
import { customerAPI } from "../services/api"

interface Address {
    _id: string
    label: string
    street?: string
    city?: string
    state?: string
    zipCode?: string
    fullAddress?: string
    coordinates?: { lat: number; lng: number }
    isDefault: boolean
    notes?: string
}

interface OrderItem {
    foodItem: { name: string; nameAr?: string; price: number; image?: string }
    quantity: number
    price: number
    size?: string
}

interface Order {
    _id: string
    orderNumber?: string
    status: string
    pricing: { total: number; subtotal: number }
    items: OrderItem[]
    restaurant: { name: string; logo?: string }
    createdAt: string
}

interface Customer {
    _id: string
    phoneNumber: string
    username: string
    fullName?: string
    email?: string
    profileImage?: string
    isActive: boolean
    registrationSource: string
    savedAddresses: Address[]
    orderStats: {
        totalOrders: number
        totalSpent: number
        completedOrders?: number
        cancelledOrders?: number
        averageOrderValue?: number
    }
    orderHistory?: Order[]
    favoriteRestaurantsDetails?: Array<{ _id: string; name: string; logo?: string }>
    favoriteFoodItemsDetails?: Array<{ _id: string; name: string; nameAr?: string; price: number; image?: string }>
    adminNotes?: string
    createdAt: string
}

interface CustomerDetailModalProps {
    customer: Customer
    onClose: () => void
    onUpdate: () => void
}

const CustomerDetailModal: React.FC<CustomerDetailModalProps> = ({ customer: initialCustomer, onClose, onUpdate }) => {
    const { t, isRTL, language } = useLanguage()
    const { toast } = useToast()

    const [customer, setCustomer] = useState<Customer>(initialCustomer)
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState<"info" | "addresses" | "orders" | "favorites">("info")
    const [editingNotes, setEditingNotes] = useState(false)
    const [adminNotes, setAdminNotes] = useState(customer.adminNotes || "")
    const [showAddAddress, setShowAddAddress] = useState(false)
    const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set())

    useEffect(() => {
        fetchCustomerDetails()
    }, [])

    const fetchCustomerDetails = async () => {
        try {
            setLoading(true)
            const response = await customerAPI.getCustomer(initialCustomer._id)
            if (response.data.success) {
                setCustomer(response.data.data)
                setAdminNotes(response.data.data.adminNotes || "")
            }
        } catch (error) {
            console.error("Failed to fetch customer details:", error)
        } finally {
            setLoading(false)
        }
    }

    const handleSaveNotes = async () => {
        try {
            await customerAPI.updateCustomer(customer._id, { adminNotes })
            toast({ title: t("success"), description: t("notesUpdated") })
            setEditingNotes(false)
            onUpdate()
        } catch (error) {
            toast({ title: t("error"), description: t("failedToUpdateNotes"), variant: "destructive" })
        }
    }

    const handleDeleteAddress = async (addressId: string) => {
        try {
            await customerAPI.deleteAddress(customer._id, addressId)
            toast({ title: t("success"), description: t("addressDeleted") })
            fetchCustomerDetails()
            onUpdate()
        } catch (error) {
            toast({ title: t("error"), description: t("failedToDeleteAddress"), variant: "destructive" })
        }
    }

    const handleSetDefaultAddress = async (addressId: string) => {
        try {
            await customerAPI.setDefaultAddress(customer._id, addressId)
            toast({ title: t("success"), description: t("defaultAddressUpdated") })
            fetchCustomerDetails()
            onUpdate()
        } catch (error) {
            toast({ title: t("error"), description: t("failedToUpdateAddress"), variant: "destructive" })
        }
    }

    const formatCurrency = (amount: number) => new Intl.NumberFormat("en-US", { style: "currency", currency: "SDG" }).format(amount)
    const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString()
    const formatDateTime = (dateString: string) => new Date(dateString).toLocaleString()

    const getAddressIcon = (label: string) => {
        switch (label.toLowerCase()) {
            case "home": return <Home className="w-4 h-4" />
            case "work": return <Briefcase className="w-4 h-4" />
            default: return <Building className="w-4 h-4" />
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case "delivered": return "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
            case "cancelled": return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
            case "pending": return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400"
            default: return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
        }
    }

    const toggleOrderExpand = (orderId: string) => {
        setExpandedOrders(prev => {
            const newSet = new Set(prev)
            if (newSet.has(orderId)) newSet.delete(orderId)
            else newSet.add(orderId)
            return newSet
        })
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-gray-200 dark:bg-gray-600 flex items-center justify-center overflow-hidden">
                            {customer.profileImage ? (
                                <img src={customer.profileImage} alt="" className="w-full h-full object-cover" />
                            ) : (
                                <span className="text-xl font-bold text-gray-600 dark:text-gray-300">
                                    {(customer.fullName || customer.username || "?").charAt(0).toUpperCase()}
                                </span>
                            )}
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                {customer.fullName || customer.username}
                            </h2>
                            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                                <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{customer.phoneNumber}</span>
                                {customer.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{customer.email}</span>}
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Stats Bar */}
                <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
                    <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{customer.orderStats.totalOrders}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("totalOrders")}</p>
                    </div>
                    <div className="text-center">
                        <p className="text-2xl font-bold text-green-600 dark:text-green-400">{formatCurrency(customer.orderStats.totalSpent)}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("totalSpent")}</p>
                    </div>
                    <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{customer.orderStats.completedOrders || 0}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("completed")}</p>
                    </div>
                    <div className="text-center">
                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(customer.orderStats.averageOrderValue || 0)}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{t("avgOrderValue")}</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-gray-700">
                    {(["info", "addresses", "orders", "favorites"] as const).map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${activeTab === tab
                                ? "text-red-600 border-b-2 border-red-600"
                                : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                                }`}
                        >
                            {t(tab === "info" ? "information" : tab)}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : (
                        <>
                            {activeTab === "info" && (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                            <p className="text-sm text-gray-500 dark:text-gray-400">{t("registrationSource")}</p>
                                            <p className="font-medium text-gray-900 dark:text-white capitalize">{customer.registrationSource.replace("_", " ")}</p>
                                        </div>
                                        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                            <p className="text-sm text-gray-500 dark:text-gray-400">{t("memberSince")}</p>
                                            <p className="font-medium text-gray-900 dark:text-white">{formatDate(customer.createdAt)}</p>
                                        </div>
                                        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                            <p className="text-sm text-gray-500 dark:text-gray-400">{t("status")}</p>
                                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${customer.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                                {customer.isActive ? t("active") : t("inactive")}
                                            </span>
                                        </div>
                                        <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                            <p className="text-sm text-gray-500 dark:text-gray-400">{t("savedAddresses")}</p>
                                            <p className="font-medium text-gray-900 dark:text-white">{customer.savedAddresses.length}</p>
                                        </div>
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{t("adminNotes")}</p>
                                            <button onClick={() => setEditingNotes(!editingNotes)} className="text-sm text-red-600 hover:text-red-700">
                                                <Edit2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                        {editingNotes ? (
                                            <div className="space-y-2">
                                                <textarea
                                                    value={adminNotes}
                                                    onChange={(e) => setAdminNotes(e.target.value)}
                                                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    rows={3}
                                                    placeholder={t("addNotesAboutCustomer")}
                                                />
                                                <div className="flex gap-2 justify-end">
                                                    <button onClick={() => setEditingNotes(false)} className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800">{t("cancel")}</button>
                                                    <button onClick={handleSaveNotes} className="px-3 py-1 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">{t("save")}</button>
                                                </div>
                                            </div>
                                        ) : (
                                            <p className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-gray-600 dark:text-gray-400 text-sm">
                                                {adminNotes || t("noNotesYet")}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            {activeTab === "addresses" && (
                                <div className="space-y-4">
                                    {customer.savedAddresses.length === 0 ? (
                                        <div className="text-center py-8">
                                            <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                            <p className="text-gray-500 dark:text-gray-400">{t("noSavedAddresses")}</p>
                                        </div>
                                    ) : (
                                        customer.savedAddresses.map((address) => (
                                            <div key={address._id} className={`p-4 rounded-lg border ${address.isDefault ? "border-red-500 bg-red-50 dark:bg-red-900/10" : "border-gray-200 dark:border-gray-700"}`}>
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-start gap-3">
                                                        <div className={`p-2 rounded-lg ${address.isDefault ? "bg-red-100 text-red-600" : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"}`}>
                                                            {getAddressIcon(address.label)}
                                                        </div>
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <p className="font-medium text-gray-900 dark:text-white">{address.label}</p>
                                                                {address.isDefault && (
                                                                    <span className="px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded-full">{t("default")}</span>
                                                                )}
                                                            </div>
                                                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                                                {address.fullAddress || `${address.street || ""}, ${address.city || ""} ${address.state || ""} ${address.zipCode || ""}`}
                                                            </p>
                                                            {address.notes && (
                                                                <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{address.notes}</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {!address.isDefault && (
                                                            <button
                                                                onClick={() => handleSetDefaultAddress(address._id)}
                                                                className="text-xs text-blue-600 hover:text-blue-700"
                                                            >
                                                                {t("setDefault")}
                                                            </button>
                                                        )}
                                                        <button
                                                            onClick={() => handleDeleteAddress(address._id)}
                                                            className="p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}

                            {activeTab === "orders" && (
                                <div className="space-y-4">
                                    {!customer.orderHistory || customer.orderHistory.length === 0 ? (
                                        <div className="text-center py-8">
                                            <ShoppingBag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                                            <p className="text-gray-500 dark:text-gray-400">{t("noOrderHistory")}</p>
                                        </div>
                                    ) : (
                                        customer.orderHistory.map((order) => (
                                            <div key={order._id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                                <div
                                                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                                    onClick={() => toggleOrderExpand(order._id)}
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
                                                            {order.restaurant?.logo ? (
                                                                <img src={order.restaurant.logo} alt="" className="w-full h-full object-cover" />
                                                            ) : (
                                                                <ShoppingBag className="w-5 h-5 text-gray-400" />
                                                            )}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-900 dark:text-white">{order.restaurant?.name || t("unknownRestaurant")}</p>
                                                            <p className="text-sm text-gray-500 dark:text-gray-400">{formatDateTime(order.createdAt)}</p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-4">
                                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                                                            {t(order.status)}
                                                        </span>
                                                        <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(order.pricing?.total || 0)}</span>
                                                        {expandedOrders.has(order._id) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                                    </div>
                                                </div>
                                                {expandedOrders.has(order._id) && (
                                                    <div className="px-4 pb-4 border-t border-gray-200 dark:border-gray-700">
                                                        <div className="pt-4 space-y-2">
                                                            {order.items?.map((item, idx) => (
                                                                <div key={idx} className="flex items-center justify-between text-sm">
                                                                    <span className="text-gray-600 dark:text-gray-400">
                                                                        {item.quantity}x {language === "ar" && item.foodItem?.nameAr ? item.foodItem.nameAr : item.foodItem?.name}
                                                                        {item.size && <span className="text-gray-400 ml-1">({item.size})</span>}
                                                                    </span>
                                                                    <span className="text-gray-900 dark:text-white">{formatCurrency(item.price * item.quantity)}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}

                            {activeTab === "favorites" && (
                                <div className="space-y-6">
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{t("favoriteRestaurants")}</h3>
                                        {!customer.favoriteRestaurantsDetails || customer.favoriteRestaurantsDetails.length === 0 ? (
                                            <p className="text-sm text-gray-500 dark:text-gray-400">{t("noFavoriteRestaurants")}</p>
                                        ) : (
                                            <div className="grid grid-cols-2 gap-3">
                                                {customer.favoriteRestaurantsDetails.map((restaurant) => (
                                                    <div key={restaurant._id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                        <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-600 overflow-hidden">
                                                            {restaurant.logo && <img src={restaurant.logo} alt="" className="w-full h-full object-cover" />}
                                                        </div>
                                                        <span className="font-medium text-gray-900 dark:text-white">{restaurant.name}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">{t("favoriteFoodItems")}</h3>
                                        {!customer.favoriteFoodItemsDetails || customer.favoriteFoodItemsDetails.length === 0 ? (
                                            <p className="text-sm text-gray-500 dark:text-gray-400">{t("noFavoriteFoodItems")}</p>
                                        ) : (
                                            <div className="grid grid-cols-2 gap-3">
                                                {customer.favoriteFoodItemsDetails.map((item) => (
                                                    <div key={item._id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                        <div className="w-10 h-10 rounded-lg bg-gray-200 dark:bg-gray-600 overflow-hidden">
                                                            {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-900 dark:text-white">{language === "ar" && item.nameAr ? item.nameAr : item.name}</p>
                                                            <p className="text-sm text-gray-500">{formatCurrency(item.price)}</p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default CustomerDetailModal
